import InventoryItemStatusType from './enums/InventoryItemStatusType';

/**
 * Бизнес-действия над оборудованием: какие из них имеют смысл прямо сейчас (7-F2A).
 *
 * Здесь всё, что можно проверить без браузера, — и именно поэтому оно здесь, а не
 * в компоненте: так же устроены inventoryCard.js, inventoryPreview.js и inventoryLifecycle.js.
 *
 * ПРАВИЛА ЗДЕСЬ — ЭТО UX, А НЕ ЗАЩИТА. Источник правды о допустимости перехода —
 * App\Services\Inventory\InventoryItemActionService: он откажет и тогда, когда запрос
 * придёт мимо интерфейса, и тогда, когда состояние предмета изменилось после того, как
 * карточку открыли. Смысл этих правил один: не предлагать человеку действие, которое
 * заведомо получит отказ.
 *
 * НЕ ПОКАЗЫВАТЬ ВМЕСТО DISABLED. Серая кнопка, на которую нельзя нажать, заставляет
 * гадать, чего не хватает; отсутствующая кнопка говорит то же самое короче. Поэтому
 * список действий собирается, а не перечисляется с признаком доступности.
 */

export const ACTION_ISSUE = 'issue';
export const ACTION_RETURN = 'return';
export const ACTION_CHANGE_HOLDER = 'change-holder';
export const ACTION_MOVE = 'move';
export const ACTION_SEND_TO_REPAIR = 'send-to-repair';
export const ACTION_RETURN_FROM_REPAIR = 'return-from-repair';
export const ACTION_MARK_BROKEN = 'mark-broken';
export const ACTION_CLEAR_FAULT = 'clear-fault';
export const ACTION_WRITE_OFF = 'write-off';

/**
 * Статусы, из которых выдавать нечего: списанного уже нет в обороте, находящегося
 * в ремонте физически нет на складе, а выдача неисправного молча стёрла бы отметку
 * о неисправности. Зеркало InventoryItemActionService::NOT_ISSUABLE_STATUSES.
 */
const NOT_ISSUABLE_STATUSES = [
    InventoryItemStatusType.IN_REPAIR,
    InventoryItemStatusType.BROKEN,
    InventoryItemStatusType.DECOMMISSIONED,
];

/**
 * Подписи действий. Глаголы в повелительном наклонении: кнопка обещает сделать, а не
 * описывает состояние.
 */
export const ACTION_TITLE = {
    [ACTION_ISSUE]: 'Выдать',
    [ACTION_RETURN]: 'Вернуть',
    [ACTION_CHANGE_HOLDER]: 'Сменить владельца',
    [ACTION_MOVE]: 'Переместить',
    [ACTION_SEND_TO_REPAIR]: 'Отправить в ремонт',
    [ACTION_RETURN_FROM_REPAIR]: 'Вернуть из ремонта',
    [ACTION_MARK_BROKEN]: 'Пометить неисправным',
    [ACTION_CLEAR_FAULT]: 'Снять отметку о неисправности',
    [ACTION_WRITE_OFF]: 'Списать',
};

/**
 * Действия, которые нельзя выполнить случайно: они меняют состояние вещи необратимо
 * или почти необратимо. Интерфейс метит их отдельно — красной кнопкой и отдельной
 * формой с обязательной причиной.
 */
export const DESTRUCTIVE_ACTIONS = [ACTION_WRITE_OFF];

/**
 * Опасное ли это действие.
 *
 * @param {string} action
 * @returns {boolean}
 */
export function isDestructiveAction(action) {
    return DESTRUCTIVE_ACTIONS.indexOf(action) !== -1;
}

/**
 * Числится ли оборудование за кем-нибудь.
 *
 * По двум взаимно исключающим ссылкам сразу: держателем может быть сотрудник
 * (responsible_person_id) либо запись справочника (holder_id), и заполнена не больше
 * чем одна из них.
 *
 * @param {Object|null} item
 * @returns {boolean}
 */
export function hasHolder(item) {
    return Boolean(item && (item.responsible_person_id || item.holder_id));
}

/**
 * Действия, которые имеет смысл предложить для этого предмета.
 *
 * ДЕРЖАТЕЛЬ РЕШАЕТ ГЛАВНОЕ. Вещь на складе выдают; вещь у человека передают дальше или
 * забирают обратно. Предлагать «Вернуть» тому, что и так лежит на складе, значит
 * обещать действие, которое сервер отклонит.
 *
 * ПЕРЕМЕСТИТЬ МОЖНО ВСЕГДА: техника переезжает и вместе с сотрудником, и со склада
 * на склад, и её состояние этому не мешает.
 *
 * @param {Object|null} item
 * @param {boolean} canEdit Право править ИМЕННО эту позицию: разрешение раздела плюс её город.
 * @returns {string[]} Коды действий в порядке показа.
 */
export function availableItemActions(item, canEdit) {
    if (!item || !canEdit) {
        return [];
    }

    const status = Number(item.status);

    // СПИСАННОЕ ТОЛЬКО ЧИТАЮТ. Над вещью, которой больше нет в обороте, делать нечего:
    // ни выдать, ни перевезти, ни списать второй раз. Карточка и история при этом
    // остаются открытыми — историю читают и после того, как вещи не стало.
    if (status === InventoryItemStatusType.DECOMMISSIONED) {
        return [];
    }

    // РЕМОНТ — СОСТОЯНИЕ С ОДНИМ ВЫХОДОМ. Вещь физически в сервисе: выдавать,
    // возвращать на склад и перевозить между городами её нельзя, а вот списать —
    // можно, ремонт нередко и заканчивается заключением «восстановлению не подлежит».
    if (status === InventoryItemStatusType.IN_REPAIR) {
        return [ACTION_RETURN_FROM_REPAIR, ACTION_WRITE_OFF];
    }

    // НЕИСПРАВНОЕ НЕ ВЫДАЮТ И НЕ ВОЗВРАЩАЮТ НА СКЛАД: обе операции ставят рабочий статус
    // и тем самым молча стёрли бы отметку о неисправности, не написав об этом в истории
    // ни строчки. Сначала снимают отметку — или увозят в ремонт. Перевозить неисправное
    // при этом можно: вещь по-прежнему у нас, в отличие от той, что уехала в сервис.
    if (status === InventoryItemStatusType.BROKEN) {
        return [ACTION_CLEAR_FAULT, ACTION_SEND_TO_REPAIR, ACTION_MOVE, ACTION_WRITE_OFF];
    }

    const actions = [];

    if (hasHolder(item)) {
        // Резерв ставят под конкретного человека, поэтому держатель у него уже есть,
        // а выдать его всё равно нужно — это и есть выход из резерва.
        if (status === InventoryItemStatusType.RESERVED) {
            actions.push(ACTION_ISSUE);
        }

        actions.push(ACTION_CHANGE_HOLDER, ACTION_RETURN);
    } else if (NOT_ISSUABLE_STATUSES.indexOf(status) === -1) {
        actions.push(ACTION_ISSUE);
    }

    actions.push(ACTION_MOVE, ACTION_MARK_BROKEN, ACTION_SEND_TO_REPAIR, ACTION_WRITE_OFF);

    return actions;
}

/**
 * Подпись действия или его код, если подписи нет.
 *
 * @param {string} action
 * @returns {string}
 */
export function actionTitle(action) {
    return ACTION_TITLE[action] ?? String(action ?? '');
}
